import {
  ClearFieldsResolver,
  CrudFieldConfig,
  CrudFieldContext,
  FieldStatePredicate,
  MetadataRow,
  MetadataValue,
  OptionResolver,
  UploadableImage,
} from "./types";

export const isFileLike = (value: unknown): value is File =>
  typeof File !== "undefined" && value instanceof File;

export const isObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value) && !isFileLike(value);

export const clonePathValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.map((entry) => clonePathValue(entry));
  }

  if (isObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, clonePathValue(entry)]),
    );
  }

  return value;
};

export const getPathSegments = (path: string) => path.split(".").filter(Boolean);

export const getValueAtPath = (source: Record<string, any>, path: string) =>
  getPathSegments(path).reduce<any>((current, segment) => {
    if (current === null || current === undefined) {
      return undefined;
    }

    if (Array.isArray(current)) {
      return current[Number(segment)];
    }

    return current[segment];
  }, source);

export const setValueAtPath = (source: Record<string, any>, path: string, value: unknown) => {
  const segments = getPathSegments(path);

  if (!segments.length) {
    return source;
  }

  const nextSource = clonePathValue(source) as Record<string, any>;
  let cursor: any = nextSource;

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    const upcomingSegment = segments[index + 1];
    const nextShouldBeArray =
      upcomingSegment !== undefined && !Number.isNaN(Number(upcomingSegment));

    if (Array.isArray(cursor)) {
      const currentIndex = Number(segment);

      if (isLast) {
        cursor[currentIndex] = value;
        return;
      }

      if (cursor[currentIndex] === undefined) {
        cursor[currentIndex] = nextShouldBeArray ? [] : {};
      } else {
        cursor[currentIndex] = clonePathValue(cursor[currentIndex]);
      }

      cursor = cursor[currentIndex];
      return;
    }

    if (isLast) {
      cursor[segment] = value;
      return;
    }

    if (cursor[segment] === undefined) {
      cursor[segment] = nextShouldBeArray ? [] : {};
    } else {
      cursor[segment] = clonePathValue(cursor[segment]);
    }

    cursor = cursor[segment];
  });

  return nextSource;
};

export const resolveFieldState = (
  state: FieldStatePredicate | undefined,
  values: Record<string, any>,
) => {
  if (typeof state === "function") {
    return state(values);
  }

  return Boolean(state);
};

export const resolveFieldOptions = (
  options: OptionResolver | undefined,
  values: Record<string, any>,
  context?: CrudFieldContext,
) => {
  if (typeof options === "function") {
    return options(values, context);
  }

  return options || [];
};

export const resolveClearedFieldPaths = (
  clearFields: ClearFieldsResolver | undefined,
  nextValue: any,
  values: Record<string, any>,
) => {
  if (!clearFields) {
    return [];
  }

  const paths = typeof clearFields === "function" ? clearFields(nextValue, values) : clearFields;

  return (paths || []).filter((path) => {
    const current = getValueAtPath(values, path);

    return current !== undefined && current !== null && String(current).trim() !== "";
  });
};

export const coerceDynamicValue = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "";
  }

  // Keep padded references such as jersey code 007 intact instead of turning them into 7.
  if (/^-?\d+(\.\d+)?$/.test(trimmed) && !/^-?0\d/.test(trimmed)) {
    return Number(trimmed);
  }

  if (trimmed === "true") {
    return true;
  }

  if (trimmed === "false") {
    return false;
  }

  return trimmed;
};

export const normalizeDateValue = (value: unknown) => {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  return value.slice(0, 10);
};

export const normalizeDateTimeValue = (value: unknown) => {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    return value;
  }

  const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (match) {
    return `${match[1]}T${match[2]}`;
  }

  return value;
};

export const resolveImagePreview = (value?: UploadableImage) =>
  typeof value === "string" ? value : "";

export const normalizeGalleryImages = (galleryImages?: string[]) => {
  const entries = (galleryImages || []).filter(Boolean);
  return entries.length > 0 ? entries : [""];
};

export const extractMetadataRows = (metadata?: Record<string, MetadataValue>) => {
  const entries = Object.entries(metadata || {})
    .filter(([key]) => !["signed", "featured"].includes(key))
    .map(([key, value]) => ({
      key,
      value: String(value ?? ""),
    }));

  return entries.length > 0 ? entries : [{ key: "", value: "" }];
};

export const buildMetadataFromRows = (
  rows: MetadataRow[],
  baseMetadata: Record<string, MetadataValue> = {},
) =>
  rows.reduce<Record<string, MetadataValue>>(
    (accumulator, row) => {
      const key = row.key.trim();

      if (!key) {
        return accumulator;
      }

      accumulator[key] = row.value.trim();
      return accumulator;
    },
    { ...baseMetadata },
  );

export const getDefaultVisibleFields = (fields: CrudFieldConfig[], values: Record<string, any>) =>
  fields.filter((field) => !resolveFieldState(field.hidden, values));
